import { useCallback, useMemo } from 'react';
import { interpolateTemplateVariableString, VariableContextMap, VariableValue } from '../../core';
import { DEPRECATED_SCOPE_PREFIX, getLinkDataContextVariables, LinkDataContext, SCOPE_PREFIX } from '../model/links';
import { ContextLinkConfig } from '../model/panels';
import { useTemplateVariableValues } from './template-variables';

function useContextLinkVariableCtx(): VariableContextMap {
  const variableValues = useTemplateVariableValues();
  return useMemo(() => {
    const variableContext: Record<string, VariableValue | undefined> = {};
    Object.keys(variableValues).forEach((key) => {
      variableContext[key] = variableValues[key]?.value;
    });

    return variableContext;
  }, [variableValues]);
}

/**
 * A hook that returns a function that can be used to interpolate a context link url.
 * It takes in an optional series context that will be used to populate the series variables.
 */
export const useInterpolateContextLinkFn = () => {
  const variableLinkContext = useContextLinkVariableCtx();
  return useCallback(
    (url: string, contextVariables?: LinkDataContext) => {
      const scopeVariables = getLinkDataContextVariables(contextVariables || {}, SCOPE_PREFIX);
      const deprecatedSeriesVariables = getLinkDataContextVariables(contextVariables || {}, DEPRECATED_SCOPE_PREFIX);

      const linkVariableContext = {
        ...variableLinkContext,
        ...scopeVariables,
        ...deprecatedSeriesVariables,
      };

      return interpolateTemplateVariableString(url, linkVariableContext);
    },
    [variableLinkContext]
  );
};

/**
 * Helper hook to get the list of variables that are used in the context links.
 * It will include variables for the context if a context link config is provided and  has scope variables
 */
export function useContextLinkVariablesSuggestions({ contextLinkConfig }: { contextLinkConfig?: ContextLinkConfig }) {
  const v = useContextLinkVariableCtx();
  let variables = Object.keys(v);
  if (contextLinkConfig) {
    variables = variables.concat(contextLinkConfig.scopeVariables.map((v) => `${SCOPE_PREFIX}.${v}`));
  }
  return variables;
}
